import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import Header from "./Header";
import Footer from "./Footer";
import { EmptyState, CardGridSkeleton } from "./ui";
import NotAvailable from "./NotAvailable";
import "../Content.css";

/**
 * The shared shell for /updates, /policies and /blog.
 *
 * All three are the same page — a titled card grid over an audience-filtered list
 * endpoint — so they share one implementation. What differs is the endpoint, how a
 * row becomes a card, and the empty-state copy.
 *
 * The list a person gets back is decided ENTIRELY by the server: a visitor is served
 * only 'general' rows. Nothing here filters, and nothing here should.
 *
 * NOTE ON COPY: audience is a property of the CONTENT, not of the reader. An update
 * can be published for the public just as legitimately as for one department, so an
 * empty section means "nothing is published", not "you are missing out — log in".
 * These pages therefore never prompt for a login. The only login-gated things in the
 * whole app are the notification bell, the profile menu and launching a dApp.
 */
const ListPage = ({ eyebrow, title, intro, endpoint, renderCard, empty }) => {
    const { isAuthenticated } = useAuth();
    const [items, setItems] = useState(null);       // null = loading
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setItems(null);
            setNotFound(false);
            try {
                const res = await apiFetch(endpoint);

                /**
                 * 404 = this section does not exist for this viewer (section-level
                 * visibility). It must NOT fall through to the empty state: that renders
                 * the section's title and intro, which both looks broken and confirms the
                 * section is there. Render the same neutral not-available page a bad URL
                 * would produce instead.
                 *
                 * An empty 200 is different and still means "nothing published here".
                 */
                if (res.status === 404) {
                    if (!cancelled) setNotFound(true);
                    return;
                }

                const data = await res.json();
                if (!cancelled) setItems(data.success ? data.data : []);
            } catch (err) {
                console.error(`Failed to load ${endpoint}`, err);
                if (!cancelled) setItems([]);
            }
        })();

        return () => { cancelled = true; };
        // Signing in widens the audience filter server-side, so the list is refetched.
        // It can also REVEAL a section that was hidden, which is why 404 is re-evaluated.
    }, [endpoint, isAuthenticated]);

    return (
        <>
            <a className="skip-link" href="#main">Skip to content</a>
            <Header />

            <main id="main" className="list-page">
                <div className="page-wrap">
                    {notFound ? (
                        // Nothing about the section is rendered here — no title, no intro,
                        // no count. Identical to what a nonexistent URL shows.
                        <NotAvailable kind="page" backTo="/" backLabel="Back to home" />
                    ) : (
                    <>
                    {/* A <div>, not a <header>: a second <header> here would expose a second
                        `banner` landmark on the page, and there may only be one. */}
                    <div className="page-head">
                        <span className="eyebrow">{eyebrow}</span>
                        <h1 className="page-title">{title}</h1>
                        {intro && <p className="page-intro">{intro}</p>}
                        {items?.length > 0 && (
                            <p className="page-count">
                                {items.length} {items.length === 1 ? empty.noun : empty.nounPlural}
                            </p>
                        )}
                    </div>

                    {items === null ? (
                        <CardGridSkeleton count={6} />
                    ) : items.length === 0 ? (
                        <EmptyState icon={empty.icon} title={empty.title} message={empty.message} />
                    ) : (
                        <div className="card-grid">
                            {items.map(renderCard)}
                        </div>
                    )}
                    </>
                    )}
                </div>
            </main>

            <Footer />
        </>
    );
};

export default ListPage;
